import { FilterQuery } from '@yes.technology/react-toolkit'
import isEqual from 'lodash/isEqual'
import merge from 'lodash/merge'
import { useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { FilterFieldsResponse } from 'shared/hooks/useFilterFields/useFilterFields'
import useNavigateMergingState from 'shared/hooks/useNavigateMergingState/useNavigateMergingState'
import { useSiteState } from 'siteState/shared'

export type UseFilterFormActionsParams = {
  fields: FilterFieldsResponse
  filterQueryTargetUuid?: string
  objectclassUuid: string
  mergeFilterQuery?: FilterQuery
}

export type UseFilterFormActionsResult = {
  onClickFilter: (query: Partial<FilterQuery>) => void
  onClearFilterQuery: (query?: Partial<FilterQuery>) => void
}

export const useFilterFormActions = ({
  fields,
  filterQueryTargetUuid,
  objectclassUuid,
  mergeFilterQuery
}: UseFilterFormActionsParams): UseFilterFormActionsResult => {
  const [, setSiteQuery] = useSiteState<Partial<FilterQuery> | undefined>(
    'filter-query',
    filterQueryTargetUuid
  )
  const [, setObjectIndex] = useSiteState<number | undefined>('object-index')
  const [, setLockObjectViewObject] = useSiteState<boolean>(
    'lock-object-view-object'
  )
  const [siteFields, setSiteFields] =
    useSiteState<FilterFieldsResponse>('filter-fields')
  const [, setFilterExecutionTime] = useSiteState<Date>('filter-execution-time')
  const [, setObjectclassUUID] = useSiteState<string>('objectclassuuid')
  const [, setRefetchFilterQuery] = useSiteState(
    'refetch-filterquery',
    filterQueryTargetUuid
  )

  const navigateMergingState = useNavigateMergingState()
  const location = useLocation()
  const currentSearch = location?.search ?? ''

  const onResetObjectViewParams = useCallback(() => {
    setObjectIndex(0)
    setLockObjectViewObject(false)
    navigateMergingState({ search: currentSearch, hash: '' }, {})
  }, [
    setObjectIndex,
    setLockObjectViewObject,
    navigateMergingState,
    currentSearch
  ])

  const onClickFilter = useCallback(
    (query: Partial<FilterQuery>) => {
      if (isEqual(fields, siteFields)) {
        setRefetchFilterQuery(true)
      }
      const mergedQuery = { ...merge(query, mergeFilterQuery) }
      setSiteQuery(mergedQuery)
      onResetObjectViewParams()
      setSiteFields(fields)
      setObjectclassUUID(objectclassUuid)
      setFilterExecutionTime(new Date())
    },
    [
      fields,
      siteFields,
      mergeFilterQuery,
      setSiteQuery,
      onResetObjectViewParams,
      setSiteFields,
      setObjectclassUUID,
      objectclassUuid,
      setFilterExecutionTime,
      setRefetchFilterQuery
    ]
  )

  const onClearFilterQuery = useCallback(
    (query?: Partial<FilterQuery>) => {
      setSiteQuery(query)
      setObjectIndex(undefined)
    },
    [setSiteQuery, setObjectIndex]
  )

  return {
    onClickFilter,
    onClearFilterQuery
  }
}
